<script setup>
import HeaderBar from '@/components/layout/HeaderBar.vue'
import CreditsModal from './components/layout/CreditsModal.vue'
import { useUserStore } from '@/stores/users'
import axios from '@/plugins/axios'
import { ref, onMounted, onUnmounted } from 'vue'

const userStore = useUserStore()

// --- Heartbeat Loading State ---
const isHeartbeatWaiting = ref(true)
const isHeartbeatError = ref(false)

const wittyPhrases = [
  'Waking up the language wizards...',
  'Brewing a fresh pot of conversation...',
  'Stretching our AI neurons...',
  'Summoning the chat spirits...',
  'Polishing your virtual accent...'
]
const wittyPhraseIndex = ref(Math.floor(Math.random() * wittyPhrases.length))

const wittyPhrase = ref('')
const typingInterval = ref(null)
const isTyping = ref(false)

function typePhrase (phrase) {
  isTyping.value = true
  wittyPhrase.value = ''
  let i = 0
  if (typingInterval.value) clearInterval(typingInterval.value)
  typingInterval.value = setInterval(() => {
    if (i < phrase.length) {
      wittyPhrase.value += phrase[i]
      i++
    } else {
      clearInterval(typingInterval.value)
      isTyping.value = false
    }
  }, 35)
}

onMounted(() => {
  typePhrase(wittyPhrases[wittyPhraseIndex.value])
  wittyInterval = setInterval(() => {
    wittyPhraseIndex.value = (wittyPhraseIndex.value + 1) % wittyPhrases.length
    typePhrase(wittyPhrases[wittyPhraseIndex.value])
  }, 10000)
})
onUnmounted(() => {
  if (wittyInterval) clearInterval(wittyInterval)
  if (typingInterval.value) clearInterval(typingInterval.value)
})

let wittyInterval = null
onMounted(() => {
  wittyInterval = setInterval(() => {
    wittyPhraseIndex.value = (wittyPhraseIndex.value + 1) % wittyPhrases.length
  }, 10000)
})
onUnmounted(() => {
  if (wittyInterval) clearInterval(wittyInterval)
})

// Ping backend on app load to wake up the server, with timeout
axios.get('/heartbeat', { timeout: 30000 })
  .then(() => {
    isHeartbeatWaiting.value = false
    isHeartbeatError.value = false
  })
  .catch((error) => {
    console.error('Heartbeat error:', error)
    isHeartbeatWaiting.value = false
    if (error.code !== 'ECONNABORTED') isHeartbeatError.value = true
  })

</script>

<template>
  <div class="flex flex-col h-screen lg:overflow-hidden">
    <template v-if="isHeartbeatWaiting || !userStore.authIsReady">
      <div class="flex flex-1 flex-col items-center justify-center bg-[#FAF7F4]">
        <img src="@/assets/logo.png" alt="PapoteAI Logo" class="w-72 h-72 mb-8 animate-wiggle" />
        <p class="text-2xl text-paletteDarkGreen font-bold mb-2">PapoteAI is loading up... (It could take up to 30 seconds)</p>
        <p class="text-lg text-paletteDarkGreen opacity-80 min-h-[1.5em] typewriter">
          {{ wittyPhrase }}<span v-if="isTyping" class="typewriter-cursor">|</span>
        </p>
      </div>
    </template>
    <template v-else-if="isHeartbeatError">
      <div class="flex flex-1 flex-col items-center justify-center bg-[#FAF7F4]">
        <img src="@/assets/PapoteAI_Error.png" alt="PapoteAI Maintenance" class="w-72 h-72 mb-8" />
        <p class="text-2xl text-paletteOrange font-bold mb-2">PapoteAI is taking a power nap!</p>
        <p class="text-lg text-paletteDarkGreen opacity-80 max-w-xl text-center">
          Our servers are currently doing their yoga stretches, maintenance, or maybe just sipping a virtual espresso.<br>
          Please check back soon—PapoteAI will be back !
        </p>
      </div>
    </template>
    <template v-else>
      <HeaderBar />
      <router-view v-slot="{ Component }">
        <template v-if="Component">
          <Suspense timeout="0">
            <component :is="Component"></component>
          </Suspense>
        </template>
      </router-view>
      <CreditsModal />
    </template>
  </div>
</template>

<style>
/* Typewriter effect for witty phrase */
.typewriter {
  display: inline-block;
  white-space: pre;
  min-width: 1.5em;
}
.typewriter-cursor {
  display: inline-block;
  width: 1ch;
  animation: blink-cursor 0.8s steps(1) infinite;
  color: #085f63;
}
@keyframes blink-cursor {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
</style>
